import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCheckCircle,
  faEnvelopeOpenText,
  faLink,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Logo from 'components/common/Logo';
import Section from 'components/common/Section';
import { ToastMessage, YupMessage } from 'constants';
import { PATH_HOME, PATH_LOGIN } from 'routes/route.path';
import authServices from 'services/auth.services';
import {
  completeEmailLinkSignIn,
  isEmailLinkSignIn,
  onSignOutFirebaseSession
} from 'services/firebase.service';
import { setAuthStore } from 'stores/auth.store';
import userServices from 'services/user.services';
import { camelCaseObjNormalize, isEmail } from 'helpers/utils';

const AUTHENTICATION_TYPE_EMAIL_LINK = 'email-link';
const EMAIL_FOR_SIGN_IN_STORAGE_KEY = 'emailForSignIn';
const SENT_LINK_MESSAGE =
  'Go to your inbox, open the CabFare email, and tap the verification link to sign in.';
const COMPLETE_LINK_ERROR = 'Could not verify email link. Please request a new sign-in link.';
const EMAIL_LINK_STEPS = [
  'Enter the email address linked to your CabFare account.',
  'Open the email from CabFare and tap the verification link.',
  "You're in. We'll take you straight to your CabFare portal."
];

const getTimezoneOffsetHours = () => -new Date().getTimezoneOffset() / 60;

const pageShellStyle = {
  background:
    'radial-gradient(circle at top left, rgba(25, 135, 84, 0.12), transparent 32%), linear-gradient(180deg, #eef5ff 0%, #f6f9fc 100%)'
};

const authCardStyle = {
  borderRadius: '24px',
  overflow: 'hidden',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 24px 70px rgba(15, 23, 42, 0.14)'
};

const infoPanelStyle = {
  background: 'linear-gradient(160deg, #0f5132 0%, #157347 100%)',
  color: '#fff',
  minHeight: '100%'
};

const infoBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.45rem 0.8rem',
  borderRadius: '999px',
  backgroundColor: 'rgba(255, 255, 255, 0.14)',
  color: '#d1fae5',
  fontSize: '0.78rem',
  fontWeight: 600,
  letterSpacing: '0.02em'
};

const formPanelStyle = {
  padding: '2.5rem 2rem'
};

const inputStyle = {
  height: '54px',
  borderRadius: '14px',
  paddingLeft: '1rem',
  borderColor: '#dbe6f3',
  boxShadow: 'none'
};

const primaryButtonStyle = {
  height: '54px',
  borderRadius: '14px',
  fontWeight: 700,
  letterSpacing: '0.01em'
};

const subtlePanelStyle = {
  padding: '0.9rem 1rem',
  borderRadius: '14px',
  backgroundColor: '#f7fafc',
  border: '1px solid #e8eef6'
};

const helperTextStyle = {
  fontSize: '0.78rem',
  fontWeight: 500,
  lineHeight: 1.35,
  marginBottom: '0.55rem'
};

export default function AuthMagicLink() {
  const navigate = useNavigate();
  const savedEmail = window.localStorage.getItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY) || '';
  const emailLink = window.location.href;
  const isCompletingLink = isEmailLinkSignIn(emailLink);
  const hasAutoCompleted = useRef(false);
  const [email, setEmail] = useState(savedEmail);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSentModal, setShowSentModal] = useState(false);

  const validateEmail = value => {
    if (!value.trim()) return YupMessage.REQUIRED;
    if (!value.trim().match(isEmail)) return YupMessage.INVALID_EMAIL;
    return '';
  };

  const loginWithPortal = async firebaseToken => {
    const responseData = await authServices.socialLogin(firebaseToken);
    const { access_token } = responseData.data;
    const sessionTime = process.env.REACT_APP_AUTH_SESSION_TIME || 3600000;
    const timeLogEnd = Date.now() + Number(sessionTime);
    const profileData = await userServices.userProfileByToken(access_token);
    const normalizeUserData = camelCaseObjNormalize(profileData?.data || {});
    const { name, email: userEmail } = normalizeUserData;

    setAuthStore({
      accessToken: access_token,
      userData: normalizeUserData,
      timeLogEnd
    });

    toast.success(`Logged in as ${name || userEmail}`, {
      theme: 'colored'
    });
    navigate(PATH_HOME);
  };

  const handleSendLink = async nextEmail => {
    await authServices.signInWithEmailLink({
      authentication_type: AUTHENTICATION_TYPE_EMAIL_LINK,
      timezone: getTimezoneOffsetHours(),
      email: nextEmail
    });

    window.localStorage.setItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY, nextEmail);
    setShowSentModal(true);
  };

  const handleCompleteLink = async nextEmail => {
    try {
      const responseData = await completeEmailLinkSignIn(nextEmail, emailLink);
      const firebaseToken = responseData.user.accessToken || (await responseData.user.getIdToken());

      window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY);
      await loginWithPortal(firebaseToken);
    } catch (err) {
      await onSignOutFirebaseSession();

      if (err?.response) {
        toast.error(ToastMessage.USER_NOT_REGISTER);
        return;
      }

      toast.error(COMPLETE_LINK_ERROR);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();

    const normalizedEmail = email.trim();
    const nextError = validateEmail(normalizedEmail);
    if (nextError) {
      setError(nextError);
      return;
    }

    try {
      setError('');
      setLoading(true);

      if (isCompletingLink) {
        await handleCompleteLink(normalizedEmail);
        return;
      }

      await handleSendLink(normalizedEmail);
    } catch (err) {
      toast.error(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Could not send the sign-in link. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isCompletingLink || !savedEmail || hasAutoCompleted.current) return;

    hasAutoCompleted.current = true;
    setLoading(true);

    handleCompleteLink(savedEmail)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [isCompletingLink, savedEmail]);

  return (
    <Section className="py-0" style={pageShellStyle}>
      <Row className="justify-content-center min-vh-100 py-5 py-lg-6 px-3">
        <Col sm={12} lg={10} xl={9} xxl={8}>
          <div className="d-flex justify-content-center mb-4">
            <Logo to="#" />
          </div>
          <Card className="border-0" style={authCardStyle}>
            <Row className="g-0">
              <Col lg={5}>
                <div className="h-100 p-4 p-lg-5" style={infoPanelStyle}>
                  <div style={infoBadgeStyle}>
                    <FontAwesomeIcon icon={faShieldAlt} />
                    Secure email sign in
                  </div>
                  <h2 className="text-white mt-4 mb-3 fs-2">
                    {isCompletingLink
                      ? 'Finish your CabFare sign in'
                      : 'Passwordless access to CabFare'}
                  </h2>
                  <p className="mb-4 text-white text-opacity-75 fs--1">
                    {isCompletingLink
                      ? 'Use the same email address that received the verification link. We verify the link and continue the login flow for you.'
                      : 'We send a secure verification link to your inbox so you can sign in without remembering another password.'}
                  </p>
                  <div className="d-flex flex-column gap-3">
                    {EMAIL_LINK_STEPS.map((step, index) => (
                      <div className="d-flex align-items-start gap-3" key={step}>
                        <div
                          className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: '2rem',
                            height: '2rem',
                            borderRadius: '999px',
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            fontWeight: 700
                          }}
                        >
                          {index + 1}
                        </div>
                        <div className="pt-1 text-white text-opacity-85 fs--1">{step}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
              <Col lg={7}>
                <Card.Body style={formPanelStyle}>
                  <div
                    className="d-inline-flex align-items-center gap-2 mb-3"
                    style={{
                      color: '#157347',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em'
                    }}
                  >
                    <FontAwesomeIcon icon={isCompletingLink ? faLink : faEnvelopeOpenText} />
                    {isCompletingLink ? 'Step 2 of 2' : 'Step 1 of 2'}
                  </div>

                  <h4 className="mb-2">
                    {isCompletingLink ? 'Complete email sign in' : 'Sign in with Email'}
                  </h4>
                  <p className="text-700 mb-4">
                    {isCompletingLink
                      ? 'Enter the email address that received your CabFare verification link.'
                      : 'Enter your email address and we will send you a CabFare verification link.'}
                  </p>

                  <div className="mb-4" style={subtlePanelStyle}>
                    <div className="d-flex align-items-start gap-3">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        style={{ color: '#157347', marginTop: '0.2rem' }}
                      />
                      <div>
                        <div className="fw-semibold text-dark mb-1">
                          {isCompletingLink ? 'Verification in progress' : 'What happens next'}
                        </div>
                        <div className="text-700 fs--1 mb-0">
                          {isCompletingLink
                            ? "If you used the same browser, you'll be signed in automatically. If not, enter your email to continue."
                            : 'We will email you a one-time sign-in link. Open it from your inbox to continue securely.'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold text-dark mb-1">Email address</Form.Label>
                      <div className="text-700" style={helperTextStyle}>
                        Use the same email address you used for the CabFare app
                      </div>
                      <Form.Control
                        placeholder="name@company.com"
                        value={email}
                        name="email"
                        onChange={({ target }) => {
                          setEmail(target.value);
                          if (error) setError('');
                        }}
                        type="email"
                        autoComplete="email"
                        disabled={loading && isCompletingLink && hasAutoCompleted.current}
                        style={inputStyle}
                      />
                      {error ? <div className="invalid-feedback d-block mt-2">{error}</div> : null}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Button
                        className="w-100"
                        type="submit"
                        disabled={loading}
                        style={primaryButtonStyle}
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Continue
                          </>
                        ) : (
                          'Continue'
                        )}
                      </Button>
                    </Form.Group>
                  </Form>

                  <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3 mt-4">
                    <Button
                      variant="link"
                      className="ps-0 text-decoration-none fw-semibold d-inline-flex align-items-center text-nowrap flex-shrink-0"
                      type="button"
                      onClick={() => navigate(PATH_LOGIN)}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                      Back to login
                    </Button>
                  </div>
                </Card.Body>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Modal centered show={showSentModal} onHide={() => setShowSentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Check your email</Modal.Title>
        </Modal.Header>
        <Modal.Body>{SENT_LINK_MESSAGE}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowSentModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </Section>
  );
}
